import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Smartphone, Copy, Check, KeyRound, RotateCcw, X,
} from "lucide-react";

export default function TwoFactor() {
  const [status, setStatus] = useState({ enabled: false, backup_remaining: 0, loading: true });
  const [setupData, setSetupData] = useState(null);
  const [step, setStep] = useState("idle"); // idle | scan | backup | disable | regen
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const reload = async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus({ ...data, loading: false });
    } catch {
      setStatus({ enabled: false, backup_remaining: 0, loading: false });
    }
  };
  useEffect(() => { reload(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setStep("scan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start 2FA setup");
    } finally { setBusy(false); }
  };

  const enable = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit code from your authenticator");
    if (!password) return toast.error("Enter your account password");
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", {
        password, secret: setupData.secret,
        totp_code: otp,
        backup_codes: setupData.backup_codes,
      });
      toast.success("Two-factor authentication enabled");
      setStep("backup");
      setOtp(""); setPassword("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to enable 2FA");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!password) return toast.error("Enter your password");
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password, totp_code: otp });
      toast.success("Two-factor authentication disabled");
      setSetupData(null); setStep("idle"); setOtp(""); setPassword("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable 2FA");
    } finally { setBusy(false); }
  };

  const copy = (txt, key) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = txt; ta.setAttribute("readonly", "");
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        return true;
      } catch (_) { return false; }
    };
    const ok = (() => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(txt).catch(fallback);
          return true;
        }
      } catch (_) { /* ignore */ }
      return fallback();
    })();
    if (ok) { setCopied(key); setTimeout(() => setCopied(null), 1500); }
    else { toast.error("Copy not allowed — please copy manually"); }
  };

  return (
    <div data-testid="admin-2fa-page">
      <PageHeader
        title="Two-Factor Authentication"
        subtitle="Add Google Authenticator (TOTP) to protect your admin login"
      />

      {/* Hero strip */}
      <div className="max-w-2xl mb-5 rounded-md overflow-hidden border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-50/40 to-white p-4 flex items-center gap-4">
        <div className="h-12 w-12 rounded-md bg-emerald-500 text-white grid place-items-center shadow-md shadow-emerald-500/30">
          <ShieldCheck size={22} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] font-bold uppercase tracking-[1.5px] text-emerald-700">Bank-grade security</div>
          <div className="text-[15px] font-bold text-gray-900">A 6-digit code at every login.</div>
          <div className="text-[12px] text-gray-600">Even if your password leaks, attackers cannot get in without your phone.</div>
        </div>
      </div>

      <div className="max-w-2xl bg-white border border-gray-200 rounded-md p-6 shadow-sm" data-testid="admin-2fa-card">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100 mb-5">
          <div className={`h-11 w-11 rounded-md flex items-center justify-center border ${status.enabled ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>
            {status.enabled ? <ShieldCheck size={22} /> : <ShieldAlert size={22} />}
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-gray-900">Google Authenticator</div>
            <div className="text-xs text-gray-500">
              Once enabled, login will ask for a 6-digit code from your authenticator app.
            </div>
          </div>
          {!status.loading && (
            <span className={`text-[10px] font-bold uppercase tracking-[1.5px] px-2.5 py-1 rounded-full ${
              status.enabled
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-amber-50 text-amber-700 border border-amber-200"
            }`} data-testid="admin-2fa-status-badge">
              {status.enabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>

        {/* IDLE — not yet enabled */}
        {step === "idle" && !status.enabled && !status.loading && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
              Your admin account is currently <b>not protected</b> by 2FA. Enable it now — it takes ~30 seconds.
            </div>
            <button
              onClick={startSetup}
              disabled={busy}
              data-testid="admin-2fa-start-setup"
              className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Smartphone size={14} /> {busy ? "Loading…" : "Set up 2FA"}
            </button>
          </div>
        )}

        {/* IDLE — enabled */}
        {step === "idle" && status.enabled && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-800">
              Your admin account is protected by 2FA. <b>{status.backup_remaining}</b> backup codes remaining.
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStep("disable")}
                data-testid="admin-2fa-disable-start"
                className="h-10 px-4 border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5">
                <ShieldAlert size={13} /> Disable 2FA
              </button>
            </div>
          </div>
        )}

        {/* SCAN — setup flow */}
        {step === "scan" && setupData && (
          <div className="space-y-4" data-testid="admin-2fa-scan-step">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 flex flex-col sm:flex-row items-center gap-4">
              <img src={setupData.qr_data_url} alt="2FA QR" className="h-44 w-44 rounded-sm bg-white p-2 border border-gray-200" />
              <div className="text-xs text-gray-600 space-y-2 flex-1">
                <p className="font-semibold text-gray-900">1. Scan with Google Authenticator</p>
                <p className="text-[11px] text-gray-500">Or enter the secret manually:</p>
                <div className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 font-mono text-[12px] text-gray-800 flex items-center gap-2">
                  <span className="flex-1 break-all">{setupData.secret}</span>
                  <button type="button" onClick={() => copy(setupData.secret, "sec")} className="text-emerald-600 hover:text-emerald-700 shrink-0">
                    {copied === "sec" ? <Check size={13} /> : <Copy size={13} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs text-gray-700 font-semibold">2. Verify — enter your password and the 6-digit code</p>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your admin password"
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
                data-testid="admin-2fa-enable-password"
              />
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="w-full h-11 px-3 border border-gray-200 rounded-sm text-center text-xl tracking-[0.4em] font-mono focus:outline-none focus:border-emerald-500"
                data-testid="admin-2fa-enable-code"
              />
            </div>

            <div className="flex gap-2">
              <button onClick={enable} disabled={busy || otp.length !== 6 || !password}
                className="flex-1 h-11 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60"
                data-testid="admin-2fa-enable-confirm">
                {busy ? "Enabling…" : "Verify & enable"}
              </button>
              <button onClick={() => { setStep("idle"); setSetupData(null); setOtp(""); setPassword(""); }}
                className="h-11 px-4 rounded-sm border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5">
                <X size={13} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* BACKUP — show codes */}
        {step === "backup" && setupData?.backup_codes && (
          <div className="space-y-4" data-testid="admin-2fa-backup-step">
            <div className="rounded-sm bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-800 flex gap-2 items-start">
              <KeyRound size={14} className="mt-0.5 shrink-0" />
              <span>
                <b>Save these backup codes in a safe place.</b> Each can only be used once if you lose access to your
                authenticator app. They will not be shown again.
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {setupData.backup_codes.map((c) => (
                <div key={c} className="bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-2 font-mono text-[13px] text-gray-800 flex items-center gap-1.5">
                  <span className="flex-1">{c}</span>
                  <button onClick={() => copy(c, c)} className="text-emerald-600 hover:text-emerald-700 shrink-0">
                    {copied === c ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => copy(setupData.backup_codes.join("\n"), "all")}
                className="h-10 px-3 rounded-sm bg-white hover:bg-gray-50 border border-gray-200 text-xs font-semibold inline-flex items-center gap-1.5">
                {copied === "all" ? <Check size={12} /> : <Copy size={12} />} Copy all
              </button>
              <button onClick={() => { setStep("idle"); setSetupData(null); }}
                className="flex-1 h-10 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold"
                data-testid="admin-2fa-backup-done">
                I've saved them
              </button>
            </div>
          </div>
        )}

        {/* DISABLE */}
        {step === "disable" && (
          <div className="space-y-3" data-testid="admin-2fa-disable-step">
            <p className="text-xs text-rose-600">Confirm your admin password and a current 6-digit code to disable 2FA.</p>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password"
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="admin-2fa-disable-password" />
            <input type="text" inputMode="numeric" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000" maxLength={6}
              className="w-full h-11 px-3 border border-gray-200 rounded-sm text-center text-xl tracking-[0.4em] font-mono"
              data-testid="admin-2fa-disable-code" />
            <div className="flex gap-2">
              <button onClick={disable} disabled={busy || otp.length !== 6 || !password}
                className="flex-1 h-11 rounded-sm bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold disabled:opacity-60"
                data-testid="admin-2fa-disable-confirm">
                {busy ? "Disabling…" : "Disable 2FA"}
              </button>
              <button onClick={() => { setStep("idle"); setOtp(""); setPassword(""); }}
                className="h-11 px-4 rounded-sm border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5">
                <X size={13} /> Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
